/**
 * The attachment system's public surface.
 *
 * `WeaponSystem` needs exactly two things from here — `WeaponLoadouts` and, through
 * it, `effective(weaponId)`. Everything else on this page exists for the gunsmith
 * screen, for the behaviour systems, and for the measurement harness.
 *
 * ## For the behaviour systems module
 *
 * Five blocks are written by attachments and read by you. They are on **every**
 * weapon, attached or not, with defaults that reproduce today's behaviour, so no
 * consumer needs to branch on whether anything is fitted:
 *
 *   `def.flinch.resist`     0 stock. 1 would be immovable; **negative is legal and
 *                           meaningful** — a quick-ADS sniper build pays with it.
 *   `def.signature`         `{ minimap, radius, tracer }`. A suppressor sets
 *                           `minimap: false`; a brake widens `radius`.
 *   `def.mobility`          `{ slideFire }`.
 *   `def.overheat`          `null`, or `{ capacity, perShot, coolRate, coolDelay,
 *                           lockTime }`. `capacity` is normalised to 1.
 *   `def.launcher`          `null`, or `{ kind: 'grenade'|'smoke', rounds, velocity,
 *                           fuse, radius, damage?|duration? }`.
 *
 * You do not have to read any of that yourself. `behaviourPatch(def)` below turns a
 * resolved weapon into exactly the config shape `src/game/behaviours/index.js`
 * publishes, and `WeaponSystem` runs your own documented cycle —
 * `ctx.behaviours.reset()` then `ctx.behaviours.apply(patch)` — every time the
 * weapon in hand or its build changes. Nothing here emits `weapon:heat` or
 * `combat:shot`; those are yours and this module stays off them.
 *
 * One mismatch worth knowing about: `def.flinch.resist` is allowed to be
 * **negative**, which is how a quick-ADS sniper build pays for its speed.
 * `Flinch.js` currently clamps `flinchResist` to `[0, maxResist]`, so a negative
 * lands as 0 and the penalty is data-only until that clamp opens downwards.
 *
 * There is also `ctx.weapons.fireInhibit`, a plain boolean checked in
 * `_handleFire` — an alternative to holding `nextShotAt` ahead of the clock, if a
 * frame-exact lockout is ever easier to express that way. Nothing sets it today.
 *
 * Plus `attachments:set { weapon, slots, points, limit, def }` whenever a build
 * changes, and `weapon:effective { weapon, def, attachments }` whenever the resolved
 * stats of a *carried* weapon change.
 */

import { WEAPONS } from '../weapons.data.js';

export { SLOT_ORDER, SLOT_LABEL, PATHS, CLAMP, CLASS_CLAMP } from './schema.js';
export { CATALOG, SLOT_CONTENTS, auditCatalog } from './catalog.js';
export { MASTERY, MASTERY_BY_WEAPON } from './mastery.js';
export { MODEL_TRAITS, OPTIC_GROUPS, slotsFor, slotListFor, fits, opticGroupFor, opticGroupPeers } from './compatibility.js';
export { resolveInto, deriveEffective, ResolveCache } from './resolver.js';
export {
  LADDERS, compatibleFor, unlockLadder, unlockLevelFor, unlocksAtWeaponLevel,
  weaponXpForLevel, weaponXpToReach, weaponLevelForXp, weaponCurveTable,
  TUNING as UNLOCK_TUNING,
} from './unlocks.js';
export { WeaponLoadouts, ALL_ATTACHMENTS, setExpansionCondition, TUNING as LOADOUT_TUNING } from './WeaponLoadouts.js';

import { ALL_ATTACHMENTS, WeaponLoadouts } from './WeaponLoadouts.js';
import { LADDERS, compatibleFor } from './unlocks.js';
import { slotListFor } from './compatibility.js';
import { SLOT_ORDER } from './schema.js';
import { MASTERY_BY_WEAPON } from './mastery.js';

/* -------------------------------------------------------------------------- */
/* the bridge to the behaviour systems                                         */
/* -------------------------------------------------------------------------- */

/**
 * Turn a resolved weapon into a `ctx.behaviours` config patch.
 *
 * This function is the whole interface between the gunsmith and the behaviour
 * systems, and it lives here rather than there on purpose. The behaviour module's
 * own header is explicit that it "knows nothing about attachments, rails or the
 * Gunsmith" — so the translation from *what a part is* to *what a system does* is
 * the gunsmith's job, and this is the one place it happens.
 *
 * `firingSignature` is the only field that is not a straight copy. The behaviour
 * side wants a scalar; this side carries a boolean and a radius in metres, which is
 * the honest shape for the gunsmith (a brake is louder, a suppressor takes you off
 * the map entirely, and those are different facts). The scalar is the weapon's own
 * radius against its own class default, and 0 when the minimap flag is off.
 *
 * It is passed **unclamped**, which means a muzzle brake sends 1.25. `Signature.js`
 * runs it through `clamp01`, so today that reads as 1 and the "louder than stock"
 * half of the signature model has no effect. That is worth knowing rather than
 * hiding: rounding it to 1 on this side would make the ceiling invisible, and the
 * day the consumer's range opens above 1 the brake starts working with no edit here.
 */
export function behaviourPatch(def) {
  const baseRadius = def.base?.signature?.radius ?? def.signature.radius ?? 45;
  const sig = def.signature.minimap ? def.signature.radius / Math.max(1e-6, baseRadius) : 0;

  const patch = {
    firingSignature: sig,
    // Passed through unclamped, for the same reason. The consumer clamps into its own
    // legal range, and a value it refuses is better than one this side rounded off.
    flinchResist: def.flinch.resist,
    thermal: { enabled: !!(def.optic?.thermalMode || def.optic?.targetHighlight) },
    laser: { enabled: !!(def.laser && def.laser.visible) },
  };

  patch.overheat = def.overheat
    ? {
        enabled: true,
        heatPerShot: def.overheat.perShot,
        coolRate: def.overheat.coolRate,
        coolDelay: def.overheat.coolDelay,
        // Was missing, so every part's authored lockout length was discarded and
        // all three came out at the default 2.75s. `capacity` stays unsent on
        // purpose: it is normalised to 1 and the bar is already a 0..1 fraction.
        lockTime: def.overheat.lockTime,
      }
    : { enabled: false };

  if (def.launcher) {
    patch.launcher = {
      mode: def.launcher.kind,
      ammo: def.launcher.rounds,
      max: def.launcher.rounds,
      muzzleVelocity: def.launcher.velocity,
      radius: def.launcher.radius,
    };
    // Only a payload that explodes carries a damage figure. Sending the default for
    // a smoke round would arm it with 130 damage the catalog never gave it.
    if (def.launcher.damage != null) patch.launcher.damage = def.launcher.damage;
  } else {
    patch.launcher = { mode: 'none', ammo: 0 };
  }

  return patch;
}

/* -------------------------------------------------------------------------- */
/* the measurement harness                                                     */
/* -------------------------------------------------------------------------- */

/**
 * `window.__atttest` — drive the real system from the console and read the numbers.
 *
 * Every method here goes through the same `WeaponLoadouts` the game does. There is
 * no test-only path into the resolver and no way to produce a build the gunsmith
 * screen could not: measuring something other than what ships is worse than not
 * measuring it, because it is measuring it *wrong* and being confident about it.
 */
export function installTestHarness(loadouts) {
  const api = {
    /** Fit a part. Returns the resolver's own result object, reasons and all. */
    equip(weaponId, slot, attId, opts) {
      // `slot` is accepted and checked rather than trusted: a caller that names the
      // wrong slot for a part has a bug, and silently doing what they meant hides it.
      const a = ALL_ATTACHMENTS[attId];
      if (a && slot && a.slot !== slot) {
        return { ok: false, reason: `wrong-slot (${attId} is ${a.slot}, not ${slot})` };
      }
      return loadouts.equip(weaponId, attId, opts);
    },
    unequip: (weaponId, slot) => loadouts.unequip(weaponId, slot),
    clear: (weaponId) => loadouts.clear(weaponId),
    unlockAll: () => loadouts.unlockAll(),
    setWeaponLevel: (weaponId, n) => loadouts.setLevel(weaponId, n),
    level: (weaponId) => loadouts.level(weaponId),
    addXp: (weaponId, n) => loadouts.addXp(weaponId, n, 'debug'),

    /** The effective definition. The object the game itself is reading. */
    resolve: (weaponId) => loadouts.effective(weaponId),

    /** The stats that matter, flattened, for eyeballing a diff between builds. */
    stats(weaponId) {
      const d = loadouts.effective(weaponId);
      return {
        weapon: weaponId,
        fireMode: d.fireMode,
        rpm: round(d.rpm, 1),
        magSize: d.magSize,
        reserve: d.maxReserve,
        pellets: d.pellets ?? 1,
        pelletSpread: round(d.pelletSpread ?? 0, 5),
        pelletSpreadAds: round((d.pelletSpread ?? 0) * (d.pelletSpreadAdsScale ?? 1), 5),
        dmgNear: round(d.damage[0][1], 2),
        dmgFar: round(d.damage[d.damage.length - 1][1], 2),
        rangeMult: round(d.rangeMult, 3),
        damageMult: round(d.damageMult, 3),
        adsIn: round(d.ads.in, 4),
        fovScale: round(d.ads.fovScale, 4),
        sensScale: round(d.ads.sensScale, 4),
        reloadTac: round(d.reload.tactical, 4),
        reloadEmpty: round(d.reload.empty, 4),
        spreadAds: d.spread.ads,
        spreadHip: round(d.spread.hip, 5),
        spreadAdsMoving: round(d.spread.adsMoving, 5),
        perShot: round(d.spread.perShot, 6),
        bloomNet: round(d.spread.perShot - d.spread.bloomDecay * (60 / d.rpm), 8),
        climb30: round(d.recoil.climb30Deg, 3),
        pitchDeg: round((d.recoil.pitch * 180) / Math.PI, 5),
        yawDeg: round((d.recoil.yaw * 180) / Math.PI, 5),
        random: round(d.recoil.random, 4),
        snr: round(d.patternSnr, 1),
        sprintOut: round(d.ergonomics.sprintOut, 4),
        switchIn: round(d.ergonomics.switchIn, 4),
        adsMoveScale: round(d.ergonomics.adsMoveScale, 4),
        zoom: d.optic.zoom,
        optic: d.optic.id ?? 'iron',
        flinch: round(d.flinch.resist, 3),
        minimap: d.signature.minimap,
        sigRadius: round(d.signature.radius, 1),
        overheat: d.overheat ? round(1 / d.overheat.perShot, 1) + ' rounds' : null,
        launcher: d.launcher?.kind ?? null,
        points: loadouts.points(weaponId),
        limit: loadouts.limitFor(weaponId),
        atts: d.attachments.join(','),
      };
    },

    /** The whole gunsmith at a glance: slots, part counts, mastery, ladder span. */
    dump() {
      const rows = [];
      for (const id of Object.keys(WEAPONS)) {
        const slots = slotListFor(id);
        const compat = compatibleFor(id);
        const ladder = LADDERS[id];
        let lo = 99;
        let hi = 0;
        for (const [attId, lv] of ladder) {
          if (ALL_ATTACHMENTS[attId]?.mastery) continue;
          lo = Math.min(lo, lv);
          hi = Math.max(hi, lv);
        }
        rows.push({
          weapon: id,
          class: WEAPONS[id].classId,
          level: loadouts.level(id),
          slots: slots.length,
          missing: SLOT_ORDER.filter((s) => !slots.includes(s)).join(',') || '-',
          parts: compat.length,
          ladder: `${lo}-${hi}`,
          mastery: MASTERY_BY_WEAPON[id],
          build: Object.values(loadouts.build(id)).join(',') || '-',
          points: loadouts.points(id),
        });
      }
      return rows;
    },

    /** Every part in a slot, with the level it lands at on a given weapon. */
    slot(weaponId, slotName) {
      return loadouts.catalogFor(weaponId).filter((r) => r.slot === slotName);
    },

    catalog: () => ALL_ATTACHMENTS,
    loadouts,
  };

  if (typeof globalThis !== 'undefined') globalThis.__atttest = api;
  return api;
}

function round(v, n) {
  if (typeof v !== 'number' || !Number.isFinite(v)) return v;
  const k = 10 ** n;
  return Math.round(v * k) / k;
}

/** Convenience: build the manager and install the harness in one call. */
export function createAttachments(ctx) {
  const loadouts = new WeaponLoadouts(ctx);
  installTestHarness(loadouts);
  return loadouts;
}
